import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getFirestore, doc, getDoc, getDocs, collection, query, where,
  setDoc, updateDoc, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Swal from 'sweetalert2';
import './StorePage.css';

const db = getFirestore();

const StarRating = ({ rating, max = 5, size = 32, interactive = false, onRate }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
      {Array.from({ length: max }, (_, i) => {
        const val = i + 1;
        const filled = (interactive ? hover || rating : rating) >= val;
        return (
          <button
            key={val}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onRate?.(val)}
            onMouseEnter={() => interactive && setHover(val)}
            onMouseLeave={() => interactive && setHover(0)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: interactive ? 'pointer' : 'default',
              fontSize: size,
              color: filled ? '#ffc107' : '#e0e0e0',
              transition: 'color 0.15s, transform 0.15s',
              lineHeight: 1,
              transform: interactive && hover === val ? 'scale(1.2)' : 'scale(1)',
            }}
          >
            <i className="fas fa-star"></i>
          </button>
        );
      })}
    </div>
  );
};

const ReviewPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userData, userCollection, loading: authLoading } = useAuth();

  const type = searchParams.get('type') || 'store';
  const productId = searchParams.get('productId');

  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [product, setProduct] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, authLoading, user?.uid]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load order
      const orderDoc = await getDoc(doc(db, userCollection, user.uid, 'purchases', orderId));
      if (!orderDoc.exists()) {
        Swal.fire('Order Not Found', 'This order does not exist or has been removed.', 'error').then(() => navigate('/orders'));
        return;
      }
      const orderData = { id: orderDoc.id, ...orderDoc.data() };
      setOrder(orderData);

      // Verify order is eligible for review:
      // Any order whose fulfillmentStatus has reached 'ready' or higher,
      // OR whose status is 'complete' / 'completed' / 'open'.
      const fs = orderData.fulfillmentStatus || 'pending';
      const st = orderData.status || 'pending';
      const statusOrder = { pending: 0, sent_to_kitchen: 1, in_preparation: 2, ready: 3, served: 4, out_for_delivery: 5, complete: 6 };
      const fsValue = statusOrder[fs] ?? 0;
      const isEligible =
        fsValue >= 3 ||               // ready, served, complete, out_for_delivery
        st === 'complete' ||
        st === 'completed' ||
        st === 'open';
      if (!isEligible) {
        Swal.fire('Not Eligible', `You can only review orders that are ready or completed. (status: ${st}, fulfillment: ${fs})`, 'warning').then(() => navigate('/orders'));
        return;
      }

      // Load store
      const storeId = orderData.locationId;
      if (storeId) {
        const storeDoc = await getDoc(doc(db, 'publicStores', storeId));
        if (storeDoc.exists()) {
          setStore({ id: storeDoc.id, ...storeDoc.data() });
        }
      }

      if (type === 'product' && productId) {
        // Verify product exists in order
        const items = orderData.items || [];
        const orderProduct = items.find((item) => item.productId === productId);
        if (!orderProduct) {
          Swal.fire('Not Found', 'This product was not part of your order.', 'error').then(() => navigate('/orders'));
          return;
        }
        setProduct({
          id: productId,
          name: orderProduct.productName,
          imageUrl: orderProduct.imageUrl,
        });

        // Check existing product review (scoped to this order)
        const prodReviewSnap = await getDocs(
          query(
            collection(db, 'publicStores', storeId, 'productReviews'),
            where('productId', '==', productId),
            where('consumerId', '==', user.uid),
            where('orderId', '==', orderId)
          )
        );
        if (!prodReviewSnap.empty) {
          const existing = prodReviewSnap.docs[0];
          setExistingReview({ id: existing.id, ...existing.data() });
          setRating(existing.data().rating || 0);
          setComment(existing.data().comment || '');
        }
      } else {
        // Check existing store review (scoped to this order)
        const reviewSnap = await getDocs(
          query(
            collection(db, 'publicStores', storeId, 'reviews'),
            where('consumerId', '==', user.uid),
            where('orderId', '==', orderId)
          )
        );
        if (!reviewSnap.empty) {
          const existing = reviewSnap.docs[0];
          setExistingReview({ id: existing.id, ...existing.data() });
          setRating(existing.data().rating || 0);
          setComment(existing.data().comment || '');
        }
      }
    } catch (error) {
      console.error('Error loading review data:', error);
      Swal.fire('Error', 'Failed to load review data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      Swal.fire('Sign In Required', 'Please log in to leave a review.', 'info');
      return;
    }
    if (rating < 1 || rating > 5) {
      Swal.fire('Rating Required', 'Please select a star rating.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      if (existingReview) {
        Swal.fire('Already Reviewed', 'You have already submitted a review for this.', 'info');
        setSubmitting(false);
        return;
      }

      const storeId = order.locationId;
      const reviewData = {
        consumerId: user.uid,
        consumerName: userData?.name || user.displayName || 'Anonymous',
        consumerEmail: user.email,
        orderId,
        rating,
        comment: comment.trim(),
        updatedAt: serverTimestamp(),
      };

      const purchaseRef = doc(db, userCollection, user.uid, 'purchases', orderId);

      if (type === 'product' && productId) {
        const ref = doc(collection(db, 'publicStores', storeId, 'productReviews'));
        await setDoc(ref, {
          ...reviewData,
          productId,
          createdAt: serverTimestamp(),
        });

        await updateDoc(purchaseRef, {
          reviewedProducts: arrayUnion(productId),
          updatedAt: serverTimestamp(),
        });

        // Recalculate product stats
        const allSnap = await getDocs(collection(db, 'publicStores', storeId, 'productReviews'));
        const all = [];
        allSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.productId === productId) all.push(data);
        });
        const total = all.length;
        const sum = all.reduce((acc, r) => acc + (r.rating || 0), 0);
        const avg = total > 0 ? Math.round((sum / total) * 10) / 10 : 0;
        await setDoc(doc(db, 'publicStores', storeId, 'productReviewStats', productId), {
          averageRating: avg,
          totalReviews: total,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Store review
        const ref = doc(collection(db, 'publicStores', storeId, 'reviews'));
        await setDoc(ref, {
          ...reviewData,
          createdAt: serverTimestamp(),
        });

        await updateDoc(purchaseRef, {
          reviewedStore: true,
          updatedAt: serverTimestamp(),
        });

        // Recalculate store stats
        const allSnap = await getDocs(collection(db, 'publicStores', storeId, 'reviews'));
        const all = [];
        allSnap.docs.forEach((d) => all.push(d.data()));
        const total = all.length;
        const sum = all.reduce((acc, r) => acc + (r.rating || 0), 0);
        const avg = total > 0 ? Math.round((sum / total) * 10) / 10 : 0;
        const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        all.forEach((r) => {
          const star = Math.min(5, Math.max(1, Math.round(r.rating || 0)));
          breakdown[star] = (breakdown[star] || 0) + 1;
        });
        await setDoc(doc(db, 'publicStores', storeId, 'reviewStats', 'summary'), {
          averageRating: avg,
          totalReviews: total,
          ratingBreakdown: breakdown,
          updatedAt: serverTimestamp(),
        });
      }

      Swal.fire('Thank You!', 'Your review has been saved.', 'success').then(() => {
        navigate(`/order/${orderId}`);
      });
    } catch (error) {
      console.error('Error saving review:', error);
      Swal.fire('Error', error.message || 'Failed to save review.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1 }} className="ff-loading-stores">
          <div className="ff-spinner"></div>
          <p>Loading review page...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const isProductReview = type === 'product' && product;
  const title = isProductReview ? `Review: ${product.name}` : `Review: ${store?.locationName || 'Store'}`;
  const subtitle = isProductReview
    ? 'How was this item? Share your experience to help others.'
    : 'How was your experience? Share your feedback to help others.';

  return (
    <div className="ff-store-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ flex: 1, padding: '32px 0' }}>
        <div className="container" style={{ maxWidth: 600 }}>
          <button
            className="btn btn-sm btn-outline-secondary mb-4"
            onClick={() => navigate(`/order/${orderId}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>Back to Order
          </button>

          <div className="text-center mb-4">
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '1.8rem',
                color: '#ff5722',
              }}
            >
              <i className="fas fa-star"></i>
            </div>
            <h3 className="font-weight-bold" style={{ fontSize: '1.4rem' }}>{title}</h3>
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>{subtitle}</p>
            {isProductReview && product?.imageUrl && (
              <img
                src={product.imageUrl}
                alt={product.name}
                style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, marginTop: 8 }}
              />
            )}
            {!isProductReview && store?.logoUrl && (
              <img
                src={store.logoUrl}
                alt={store.locationName}
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, marginTop: 8, border: '2px solid #eee' }}
              />
            )}
          </div>

          <div
            className="card"
            style={{ borderRadius: 16, border: '1px solid #eee', padding: '24px 28px' }}
          >
            {existingReview ? (
              <div className="text-center">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: '#d4edda',
                    color: '#155724',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    fontSize: '1.5rem',
                  }}
                >
                  <i className="fas fa-check"></i>
                </div>
                <h5 className="font-weight-bold mb-2">Already Reviewed</h5>
                <p className="text-muted small mb-3">
                  You have already submitted a review for this {isProductReview ? 'item' : 'order'}.
                </p>
                <div style={{ marginBottom: 16 }}>
                  <StarRating rating={rating} size={36} />
                  <div style={{ textAlign: 'center', marginTop: 6, fontSize: '0.85rem', color: '#888' }}>
                    {rating === 1 && 'Terrible'}
                    {rating === 2 && 'Poor'}
                    {rating === 3 && 'Average'}
                    {rating === 4 && 'Very Good'}
                    {rating === 5 && 'Excellent'}
                  </div>
                </div>
                {comment && (
                  <div
                    style={{
                      background: '#f8f9fa',
                      padding: 12,
                      borderRadius: 10,
                      fontSize: '0.9rem',
                      color: '#555',
                      marginBottom: 16,
                      textAlign: 'left',
                    }}
                  >
                    {comment}
                  </div>
                )}
                <button
                  className="btn btn-primary"
                  style={{
                    background: '#ff5722',
                    borderColor: '#ff5722',
                    padding: '10px 28px',
                    borderRadius: 10,
                    fontWeight: 600,
                  }}
                  onClick={() => navigate(`/order/${orderId}`)}
                >
                  <i className="fas fa-arrow-left mr-2"></i>Back to Order
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 700, color: '#555', marginBottom: 12, textAlign: 'center' }}>
                    Your Rating
                  </label>
                  <StarRating rating={rating} interactive size={40} onRate={setRating} />
                  <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.85rem', color: '#888', minHeight: 20 }}>
                    {rating === 1 && 'Terrible'}
                    {rating === 2 && 'Poor'}
                    {rating === 3 && 'Average'}
                    {rating === 4 && 'Very Good'}
                    {rating === 5 && 'Excellent'}
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 700, color: '#555', marginBottom: 8 }}>
                    Your Review
                  </label>
                  <textarea
                    className="ff-form-control"
                    rows={4}
                    placeholder={isProductReview ? 'How did this item taste? Would you order it again?' : 'How was the service, delivery, and overall experience?'}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 14,
                      borderRadius: 12,
                      border: '2px solid #eee',
                      fontSize: '0.95rem',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-primary"
                    style={{
                      background: '#ff5722',
                      borderColor: '#ff5722',
                      padding: '10px 28px',
                      borderRadius: 10,
                      fontWeight: 600,
                    }}
                    onClick={handleSubmit}
                    disabled={submitting || rating === 0}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane mr-2"></i>
                        Submit Review
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    style={{ padding: '10px 20px', borderRadius: 10 }}
                    onClick={() => navigate(`/order/${orderId}`)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReviewPage;